import { ImportValidationErrorItem } from "@app/shared/components/import-validation-errors-modal/import-validation-errors-modal.types";
import {
  PackageImportReport,
  PackageImportRowStatus,
} from "../../../models/enrichment-import-export.model";

const ROW_STATUS_LABEL: Record<PackageImportRowStatus, string> = {
  saved: "Saved",
  validated: "Validated",
  no_v104_master: "No 1.0.4 master price for this SKU",
  no_product_code: "The 1.0.4 master price has no product code",
  no_v200_master: "No 2.0.0 master price for this product code",
  v200_master_deleted: "The 2.0.0 master price is deleted",
  no_po_master: "No master package carries this price",
  not_a_package: "The package is not a Mobile package",
  not_sold_on_online_store: "The package is not sold on Online Store",
  sku_prefix_not_O_or_P: "SKU must start with O or P",
  conflict: "An earlier row already wrote this price",
  failed: "Not saved",
};

const OK_STATUSES: ReadonlySet<string> = new Set<PackageImportRowStatus>([
  "saved",
  "validated",
]);

/** True when any row failed to match or save. Unmatched SKUs belong to no
 *  offering, so `failedOfferings` alone can read 0 while rows failed. */
export function packageImportHasFailedRows(
  report: PackageImportReport,
): boolean {
  return report.results.some((r) => !OK_STATUSES.has(r.status));
}

function statusLabel(status: string): string {
  return ROW_STATUS_LABEL[status as PackageImportRowStatus] ?? status;
}

/** One modal line per row that failed or carries warnings, in sheet order. */
export function packageImportProblems(
  report: PackageImportReport,
): ImportValidationErrorItem[] {
  return report.results
    .filter((r) => !OK_STATUSES.has(r.status) || r.warnings.length > 0)
    .map((r) => ({
      primary: `Row ${r.row}`,
      secondary: r.sku || undefined,
      message: [
        `${statusLabel(r.status)} (${r.status})`,
        ...(r.error ? [r.error] : []),
        ...r.warnings,
      ].join("; "),
    }));
}

export function packageImportSummary(report: PackageImportReport): string {
  const done = report.dryRun ? "validated" : "saved";
  const counted = `${report.savedOfferings} of ${report.offerings} package(s) ${done}`;
  const failed =
    report.failedOfferings > 0 ? `, ${report.failedOfferings} failed` : "";
  const dryRunTag = report.dryRun ? " (dry run, nothing was written)" : "";
  return `${counted}${failed}${dryRunTag}`;
}
